<template>
  <div class="report-page">
    <div class="filter-card">
      <div class="fc-row fc-row-primary">
        <div class="field">
          <label class="field-lbl">ระดับ</label>
          <b-form-select
            v-model="tierFilter"
            :options="tierOptions"
            size="sm"
            class="field-input-native"
          ></b-form-select>
        </div>

        <div class="field">
          <label class="field-lbl">สาขา</label>
          <b-form-select
            v-model="branchFilter"
            :options="branchOptions"
            size="sm"
            class="field-input-native"
          ></b-form-select>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <b-alert :show="!!errorMessage" variant="danger">{{ errorMessage }}</b-alert>

        <div class="toolbar d-flex align-items-center flex-wrap mb-2">
          <div class="pager d-flex align-items-center">
            <b-button
              size="sm"
              variant="outline-secondary"
              :disabled="currentPage <= 1 || isLoading"
              @click="prevPage"
            >
              <i class="fas fa-chevron-left"></i>
            </b-button>
            <span class="mx-2">หน้า {{ currentPage }}</span>
            <b-button
              size="sm"
              variant="outline-secondary"
              :disabled="isLastPage || isLoading"
              @click="nextPage"
            >
              <i class="fas fa-chevron-right"></i>
            </b-button>
          </div>
          <div class="spacer flex-grow-1"></div>
          <b-dropdown text="ส่งออก" size="sm" variant="outline-success" right :disabled="!report.length">
            <b-dropdown-item @click="exportExcel">
              <i class="fas fa-file-excel mr-2"></i>Excel
            </b-dropdown-item>
            <b-dropdown-item @click="exportPdf">
              <i class="fas fa-file-pdf mr-2"></i>PDF
            </b-dropdown-item>
          </b-dropdown>
        </div>

        <b-table
          id="tierRosterTable"
          :items="report"
          :fields="fields"
          :busy="isLoading"
          responsive
          hover
          show-empty
          empty-text="ไม่มีข้อมูลให้แสดง"
        >
          <template #cell(tierName)="{ value }">{{ value || "—" }}</template>
          <template #cell(qualifiedAmount)="{ value }">{{ formatBaht(value) }}</template>
          <template #cell(attainedDt)="{ value }">{{ fmtDate(value) }}</template>
          <template #cell(status)="{ item }">
            <b-badge :variant="item.status === 'GRACE' ? 'info' : 'success'">
              {{ statusLabel(item.status) }}
            </b-badge>
            <small v-if="item.status === 'GRACE' && item.graceUntilDt" class="text-info d-block">
              ถึง {{ fmtDate(item.graceUntilDt) }}
            </small>
          </template>
        </b-table>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import { tierErrorMessage } from "@/utils/tierErrors";
import { exportTableToExcel, exportReportToPdf } from "@/utils/reportExport";
import { rosterStatusLabel } from "@/utils/tierReportDisplay";

// Backend default (see getTierReport.js's DEFAULT_ROSTER_LIMIT) — kept in
// sync deliberately rather than left to each side's own default so a page
// here always lines up with a page there.
const LIMIT = 50;

export default {
  name: "TierRosterReport",
  data() {
    return {
      isLoading: false,
      errorMessage: "",
      report: [],
      allTiers: [],
      tierFilter: null,
      branchFilter: null,
      currentPage: 1,
      limit: LIMIT,
      reportTitle: "รายงานคนไข้ตามระดับ",
      reportNameEn: "TierRoster",
      fields: [
        { key: "hn", label: "HN" },
        { key: "patientName", label: "ชื่อคนไข้" },
        { key: "tierName", label: "ระดับ" },
        { key: "qualifiedAmount", label: "ยอดสะสม (บาท)", class: "text-right" },
        { key: "attainedDt", label: "วันที่ได้ระดับ" },
        { key: "status", label: "สถานะ" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      clinicInfo: "moduleBranch/getClinicInfo",
      getClinicList: "moduleUser/getClinicList",
    }),
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },
    currentClinic() {
      return (this.getClinicList || []).find(c => c.code === this.clinicUrl);
    },
    branches() {
      return this.currentClinic?.branches || [];
    },
    tierOptions() {
      return [
        { value: null, text: "ทุกระดับ" },
        ...this.allTiers.map(t => ({ value: t.id, text: t.name })),
      ];
    },
    branchOptions() {
      return [
        { value: null, text: "ทุกสาขา" },
        ...this.branches.map(b => ({ value: b.id, text: b.name })),
      ];
    },
    // The backend does not return a total count for this report (see
    // tier_repo/getTierRoster.js — plain LIMIT/OFFSET, no COUNT(*) query),
    // so there is no total-rows to drive a numbered b-pagination against.
    // A page shorter than the requested limit is the only signal available
    // that it was the last page.
    isLastPage() {
      return this.report.length < this.limit;
    },
  },
  methods: {
    ...mapActions("moduleTier", ["getTierReport", "listTiers"]),
    ...mapActions("moduleUser", ["fetchClinicList"]),

    formatBaht(value) {
      return Number(value || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    // DD/MM/YYYY (unambiguous numeric Gregorian year), matching the
    // convention already established for tier screens — see
    // PatientTierPanel.vue's fmtDate / TierBadge.vue's graceUntilLabel.
    // moment's "th" locale does NOT give a Buddhist-Era year despite
    // reading like one, so it is never used for a date shown to staff.
    fmtDate(value) {
      return value ? moment(value).format("DD/MM/YYYY") : "—";
    },
    statusLabel(status) {
      return rosterStatusLabel(status);
    },

    async load() {
      if (!this.clinicUrl) return;
      this.isLoading = true;
      this.errorMessage = "";
      try {
        // offset MUST be computed as a plain number and forwarded as-is —
        // (currentPage - 1) * limit is 0 on page 1, and 0 is a valid,
        // meaningful offset (the first page), never something to drop with
        // a falsy check (`offset || undefined` would silently turn page 1
        // into "no offset" and moduleTier's action / the backend both treat
        // that as "start from 0" only by coincidence — see
        // stores/tier.js's getTierReport and getTierReport.js's own
        // comment on this exact trap). Passing the number directly, always,
        // sidesteps the trap entirely rather than relying on that
        // coincidence.
        const offset = (this.currentPage - 1) * this.limit;
        this.report = await this.getTierReport({
          clinicUrl: this.clinicUrl,
          type: "roster",
          tierId: this.tierFilter,
          branchId: this.branchFilter,
          limit: this.limit,
          offset,
        });
      } catch (error) {
        this.errorMessage = tierErrorMessage(error) || "โหลดรายงานไม่สำเร็จ";
      } finally {
        this.isLoading = false;
      }
    },

    prevPage() {
      if (this.currentPage <= 1 || this.isLoading) return;
      this.currentPage -= 1;
      this.load();
    },
    nextPage() {
      if (this.isLastPage || this.isLoading) return;
      this.currentPage += 1;
      this.load();
    },

    exportOptions() {
      return {
        tableId: "tierRosterTable",
        // The export only ever captures the currently loaded page — say so
        // in the title rather than let it read like a full roster.
        reportTitle: `${this.reportTitle} (หน้า ${this.currentPage})`,
        reportNameEn: this.reportNameEn,
        clinicName: this.clinicInfo && this.clinicInfo.name,
      };
    },
    exportExcel() {
      exportTableToExcel(this.exportOptions());
    },
    exportPdf() {
      exportReportToPdf(this.exportOptions());
    },
  },
  watch: {
    tierFilter() {
      this.currentPage = 1;
      this.load();
    },
    branchFilter() {
      this.currentPage = 1;
      this.load();
    },
  },
  async created() {
    // includeInactive: true — a patient can be pinned to (or historically
    // hold) a tier that has since been deactivated; the filter dropdown
    // must still be able to name it. Mirrors PatientTierPanel.vue.
    // .catch, matching branchPromise below: without it a rejection here
    // throws out of created() entirely, this.load() never runs, and the
    // roster silently renders its empty state ("ไม่มีข้อมูลให้แสดง") with no
    // errorMessage — indistinguishable from an actually-empty roster. Falling
    // back to allTiers: [] just means the tier filter dropdown loses its
    // labels; it must not also suppress the report itself.
    const tierPromise = this.listTiers({ clinicUrl: this.clinicUrl, includeInactive: true })
      .then(tiers => {
        this.allTiers = tiers;
      })
      .catch(() => {
        this.allTiers = [];
      });
    const branchPromise = this.fetchClinicList().catch(() => {});
    await Promise.all([tierPromise, branchPromise]);
    this.load();
  },
};
</script>

<style scoped>
.spacer {
  min-width: 8px;
}
</style>
